import React, { useState, useEffect, useCallback } from 'react';
import { studentApi, therapistApi } from '../services/api';

interface SessionStatistics {
  students: StudentSessionData[];
  summary: {
    totalStudents: number;
    activeStudents: number;
    studentsWithSessions: number;
    totalSessions: number;
    averageSessionsPerStudent: number;
  };
  filters: {
    startDate?: string;
    endDate?: string;
    active?: string;
    therapistId?: number;
    scheduleId?: number;
  };
}

interface StudentSessionData {
  id: number;
  first_name: string;
  last_name: string;
  name: string;
  grade_level?: string;
  therapy_needs?: string;
  is_active: boolean;
  total_sessions: number;
  therapy_sessions: number;
  group_sessions: number;
  first_session_date?: string;
  last_session_date?: string;
  unique_schedules: number;
  unique_therapists: number;
  therapist_names?: string;
  scheduleBreakdowns: ScheduleBreakdown[];
}

interface ScheduleBreakdown {
  scheduleId: number;
  weekStartDate: string;
  weekEndDate: string;
  therapistName: string;
  totalSessions: number;
  therapySessions: number;
  groupSessions: number;
}

interface Therapist {
  id: number;
  name: string;
}

const StudentSessions: React.FC = () => {
  const [data, setData] = useState<SessionStatistics | null>(null);
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedStudent, setExpandedStudent] = useState<number | null>(null);

  // Filters
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    active: 'true',
    therapistId: '',
    search: '',
  });

  const loadTherapists = async () => {
    try {
      const response = await therapistApi.getAll({ active: true, limit: 100 });
      setTherapists(response.data.therapists);
    } catch (err: any) {
      console.error('Error loading therapists:', err);
    }
  };

  const loadSessionStatistics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: any = {};

      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.active !== 'all') params.active = filters.active;
      if (filters.therapistId) params.therapistId = parseInt(filters.therapistId);
      if (filters.search) params.search = filters.search;

      const response = await studentApi.getSessionStatistics(params);
      setData(response.data);
    } catch (err: any) {
      console.error('Error loading session statistics:', err);
      setError(err.error || 'Failed to load session statistics');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadTherapists();
    loadSessionStatistics();
  }, [loadSessionStatistics]);

  useEffect(() => {
    loadSessionStatistics();
  }, [filters, loadSessionStatistics]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const toggleStudentExpansion = (studentId: number) => {
    setExpandedStudent(expandedStudent === studentId ? null : studentId);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        flexDirection: 'column'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '1rem'
        }}></div>
        <p>Loading session statistics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '2rem',
        color: '#e74c3c'
      }}>
        <p>Error: {error}</p>
        <button
          onClick={loadSessionStatistics}
          style={{
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer',
            marginTop: '1rem'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) {
    return <div>No data available</div>;
  }

  return (
    <div style={{ padding: '1rem' }}>
      <h1 style={{
        marginBottom: '2rem',
        color: '#2c3e50',
        borderBottom: '3px solid #3498db',
        paddingBottom: '0.5rem'
      }}>
        Student Session Analytics
      </h1>

      {/* Filters */}
      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        marginBottom: '2rem'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>Filters</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Search Students:
            </label>
            <input
              type="text"
              placeholder="Search by name..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Start Date:
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              End Date:
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Student Status:
            </label>
            <select
              value={filters.active}
              onChange={(e) => handleFilterChange('active', e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            >
              <option value="all">All Students</option>
              <option value="true">Active Students</option>
              <option value="false">Inactive Students</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Therapist:
            </label>
            <select
              value={filters.therapistId}
              onChange={(e) => handleFilterChange('therapistId', e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            >
              <option value="">All Therapists</option>
              {therapists.map(therapist => (
                <option key={therapist.id} value={therapist.id}>
                  {therapist.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          backgroundColor: '#3498db',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{data.summary.totalStudents}</div>
          <div>Total Students</div>
        </div>

        <div style={{
          backgroundColor: '#27ae60',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{data.summary.studentsWithSessions}</div>
          <div>Students with Sessions</div>
        </div>

        <div style={{
          backgroundColor: '#e67e22',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{data.summary.totalSessions}</div>
          <div>Total Sessions</div>
        </div>

        <div style={{
          backgroundColor: '#9b59b6',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{data.summary.averageSessionsPerStudent}</div>
          <div>Avg Sessions/Student</div>
        </div>
      </div>

      {/* Student List */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
      }}>
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '1rem',
          borderBottom: '1px solid #dee2e6'
        }}>
          <h3 style={{ margin: 0, color: '#2c3e50' }}>
            Student Session Details ({data.students.length} students)
          </h3>
        </div>

        {data.students.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
            No students found with the current filters.
          </div>
        ) : (
          <div>
            {data.students.map((student, index) => (
              <div key={student.id} style={{
                borderBottom: index < data.students.length - 1 ? '1px solid #dee2e6' : 'none'
              }}>
                {/* Student Row */}
                <div
                  style={{
                    padding: '1rem',
                    cursor: 'pointer',
                    backgroundColor: expandedStudent === student.id ? '#f8f9fa' : 'white',
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr auto',
                    gap: '1rem',
                    alignItems: 'center'
                  }}
                  onClick={() => toggleStudentExpansion(student.id)}
                >
                  <div>
                    <div style={{ fontWeight: '600', color: '#2c3e50' }}>
                      {student.first_name} {student.last_name}
                      {!student.is_active && (
                        <span style={{
                          marginLeft: '0.5rem',
                          padding: '0.25rem 0.5rem',
                          backgroundColor: '#e74c3c',
                          color: 'white',
                          fontSize: '0.75rem',
                          borderRadius: '4px'
                        }}>
                          Inactive
                        </span>
                      )}
                    </div>
                    {student.grade_level && (
                      <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                        Grade: {student.grade_level}
                      </div>
                    )}
                  </div>

                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '600', fontSize: '1.25rem', color: '#3498db' }}>
                      {student.total_sessions}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>Total</div>
                  </div>

                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '600', color: '#27ae60' }}>
                      {student.therapy_sessions}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>Therapy</div>
                  </div>

                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '600', color: '#e67e22' }}>
                      {student.group_sessions}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>Group</div>
                  </div>

                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '600' }}>{student.unique_schedules}</div>
                    <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>Schedules</div>
                  </div>

                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '600' }}>{student.unique_therapists}</div>
                    <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>Therapists</div>
                  </div>

                  <div style={{
                    fontSize: '1.25rem',
                    color: '#6c757d',
                    transform: expandedStudent === student.id ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                  }}>
                    ▼
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedStudent === student.id && (
                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#f8f9fa',
                    borderTop: '1px solid #dee2e6'
                  }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                      gap: '1.5rem'
                    }}>
                      {/* Student Info */}
                      <div>
                        <h4 style={{ marginTop: 0, color: '#2c3e50' }}>Student Information</h4>
                        <div style={{ fontSize: '0.875rem' }}>
                          <div><strong>Name:</strong> {student.first_name} {student.last_name}</div>
                          {student.grade_level && <div><strong>Grade:</strong> {student.grade_level}</div>}
                          {student.therapy_needs && <div><strong>Therapy Needs:</strong> {student.therapy_needs}</div>}
                          {student.therapist_names && <div><strong>Therapists:</strong> {student.therapist_names}</div>}
                          <div><strong>First Session:</strong> {formatDate(student.first_session_date)}</div>
                          <div><strong>Last Session:</strong> {formatDate(student.last_session_date)}</div>
                        </div>
                      </div>

                      {/* Schedule Breakdowns */}
                      {student.scheduleBreakdowns.length > 0 && (
                        <div>
                          <h4 style={{ marginTop: 0, color: '#2c3e50' }}>Schedule Breakdown</h4>
                          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {student.scheduleBreakdowns.map((breakdown, idx) => (
                              <div key={idx} style={{
                                padding: '0.75rem',
                                backgroundColor: 'white',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                marginBottom: '0.5rem',
                                fontSize: '0.875rem'
                              }}>
                                <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                                  {breakdown.therapistName}
                                </div>
                                <div style={{ color: '#6c757d', marginBottom: '0.25rem' }}>
                                  {formatDate(breakdown.weekStartDate)} - {formatDate(breakdown.weekEndDate)}
                                </div>
                                <div style={{ display: 'flex', gap: '1rem' }}>
                                  <span><strong>{breakdown.totalSessions}</strong> total</span>
                                  <span><strong>{breakdown.therapySessions}</strong> therapy</span>
                                  <span><strong>{breakdown.groupSessions}</strong> group</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentSessions;